/**
 * Attribution for the specialist bench.
 *
 * These are instrument channels, not colleagues: a three-letter code in a box
 * and an 8px glyph. No avatars, no names, no personality — an agent that looks
 * like a person invites the user to trust it like one.
 *
 * The glyph is not decoration. It encodes the agent's *domain family*, so a
 * reader scanning a provenance gutter can see at a glance whether an answer
 * rests on documents, on space, on the subsurface, or on governance.
 */

export type AgentFamily = 'documents' | 'space' | 'subsurface' | 'governance' | 'time' | 'routing';

export interface AgentDefinition {
  /** Three-letter channel code, e.g. `ЗАП`. */
  readonly code: string;
  readonly name: string;
  readonly family: AgentFamily;
  /** The single question this specialist answers. */
  readonly question: string;
}

export const AGENTS = {
  ARH: { code: 'АРХ', name: 'Архивариус', family: 'documents', question: 'Что существует и в каком состоянии?' },
  PRV: { code: 'ПРВ', name: 'Провенанс', family: 'documents', question: 'Откуда мы это знаем?' },
  TER: { code: 'ТЕР', name: 'Терминолог', family: 'documents', question: 'Это одно название или два?' },
  GEO: { code: 'ГЕО', name: 'Геодезист', family: 'space', question: 'Где это — и насколько мы уверены?' },
  KAR: { code: 'КАР', name: 'Картограф', family: 'space', question: 'Как это выглядит в пространстве?' },
  SKV: { code: 'СКВ', name: 'Скважинник', family: 'subsurface', question: 'Что показало бурение?' },
  MIN: { code: 'МИН', name: 'Минералог', family: 'subsurface', question: 'Из чего это состоит?' },
  ZAP: { code: 'ЗАП', name: 'Запасовед', family: 'subsurface', question: 'Сколько там — и кто это утвердил?' },
  VAL: { code: 'ВАЛ', name: 'Валидатор', family: 'governance', question: 'Что не сходится?' },
  RET: { code: 'РЕЦ', name: 'Рецензент', family: 'governance', question: 'Можно ли считать это истиной?' },
  HRO: { code: 'ХРО', name: 'Хронист', family: 'time', question: 'Как ответ менялся во времени?' },
  DIR: { code: 'ДИР', name: 'Дирижёр', family: 'routing', question: 'Кто отвечает и согласны ли они?' },
} as const satisfies Record<string, AgentDefinition>;

export type AgentKey = keyof typeof AGENTS;

function FamilyGlyph({ family }: { family: AgentFamily }) {
  const common = { width: 9, height: 9, viewBox: '0 0 9 9', 'aria-hidden': true } as const;
  switch (family) {
    case 'documents':
      return (
        <svg {...common}>
          <rect x="0.9" y="0.9" width="7.2" height="7.2" fill="none" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      );
    case 'space':
      return (
        <svg {...common}>
          <path d="M4.5 1 L8.4 8 H.6 Z" fill="none" stroke="currentColor" strokeWidth="1.2" />
        </svg>
      );
    case 'subsurface':
      return (
        <svg {...common}>
          <circle cx="4.5" cy="4.5" r="3.6" fill="none" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      );
    case 'governance':
      return (
        <svg {...common}>
          <path d="M4.5 .8 L8.2 4.5 L4.5 8.2 L.8 4.5 Z" fill="none" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      );
    case 'time':
      return (
        <svg {...common}>
          <rect x="0.6" y="3.4" width="7.8" height="2.2" fill="currentColor" />
        </svg>
      );
    case 'routing':
      return (
        <svg {...common}>
          <path d="M4.5 .7 V8.3 M.7 4.5 H8.3" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      );
  }
}

export interface AgentMarkProps {
  agent: AgentKey;
  /** Show the full name beside the code. Off inside dense gutters. */
  withName?: boolean;
}

export default function AgentMark({ agent, withName = false }: AgentMarkProps) {
  const definition = AGENTS[agent];
  return (
    <span className="lg-agent" title={`${definition.name} — ${definition.question}`}>
      <FamilyGlyph family={definition.family} />
      <b className="lg-agent__code">{definition.code}</b>
      {withName ? definition.name : null}
    </span>
  );
}
